import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Col, Row, Spinner } from "react-bootstrap"
import { Page } from "@src/components"
import { useTranslation } from "react-i18next"
import { usePartner } from "@src/partner"
import { useFleets } from "@src/settings"
import { colors } from "@src/constants"
import { StatisticCard } from "./components"
import { centerAlign } from "@src/device/devicedetail/tabs/components"
import "./Dashboard.css"
import { StyleSheet } from "@src/types"
import { useUser } from "@src/user"
import { getCo2, getDistance, getSpeed, getTrips } from "@src/api/requests/statistics"
import { getDevice } from "@src/api"

type STATISTICS_DATA = {
  devices_count: string
  trip_count: string
  movement_count: string
  movement_lasthour_count: string
  valid_position_count: string
  valid_position_lastday_count: string
  no_servicebooking_count: string
  expiring_servicebooking_count: string
  critical_battery_devices_count: string
  locked_devices_count: string
  unlocked_devices_count: string
  theft_reported_devices_count: string
  avg_distance: string
  total_distance: string
  avg_co2: string
  total_co2: string
  avg_trips: string
  total_trips: string
  avg_speed: string
  total_speed: string
}

const defaultStatisticsData: STATISTICS_DATA = {
  devices_count: "",
  trip_count: "",
  movement_count: "",
  movement_lasthour_count: "",
  valid_position_count: "",
  valid_position_lastday_count: "",
  no_servicebooking_count: "",
  expiring_servicebooking_count: "",
  critical_battery_devices_count: "",
  locked_devices_count: "",
  unlocked_devices_count: "",
  theft_reported_devices_count: "",
  avg_distance: "",
  total_distance: "",
  avg_co2: "",
  total_co2: "",
  avg_trips: "",
  total_trips: "",
  avg_speed: "",
  total_speed: "",
}

const styles: StyleSheet = {
  backgroundDrop: {
    backgroundColor: "rgba(0,0,0,0.3)",
    width: "100%",
    height: "100%",
    zIndex: 1,
    top: 0,
    left: 0,
    bottom: 0,
    borderRadius: ".25rem",
    position: "absolute",
  },
  loaderView: {
    paddingTop: 10,
    paddingLeft: 5,
    paddingRight: 5,
    display: "flex",
    backgroundColor: colors.smokeWhite,
    height: "65px",
    zIndex: 999,
    borderRadius: 8,
    flexDirection: "column",
  },
  borderStyles: {
    flex: 1,
    paddingLeft: "20px",
    paddingRight: "20px",
    paddingTop: "14px",
    paddingBottom: "14px",
    borderWidth: 1,
    marginTop: "20px",
    borderColor: "#F0F0F0",
    borderRadius: "10px",
    height: "inherit",
  },
  cardTitle: {
    color: "#636E72",
    fontSize: "17px",
    marginTop: "10px",
    marginBottom: "10px",
    fontWeight: "bold",
  },
}

export const DashboardScreen: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { partner } = usePartner()
  const [loading, setLoading] = useState<boolean>(true)
  const [statisticsData, setStatisticsData] = useState<STATISTICS_DATA>(defaultStatisticsData)
  const { fleet } = useFleets()
  const { distanceConversion } = useUser()
  const [topRowCounts, setTopRowCounts] = useState<{
    total: number
    active: number
    locked: number
    stolen: number
    expiring: number
    noService: number
    criticalBatteryActive: number
  }>({
    total: 0,
    active: 0,
    locked: 0,
    stolen: 0,
    expiring: 0,
    noService: 0,
    criticalBatteryActive: 0,
  })

  useEffect(() => {
    if (partner) {
      setLoading(true)
      Promise.all([getStatistics(), refreshTopRowCounts()]).finally(() => {
        setLoading(false)
      })
    }
  }, [partner, fleet])

  const retryRequest = async (fn: Function, retries: number = 3, delay: number = 1000) => {
    try {
      return await fn()
    } catch (error) {
      if (retries === 0 || error?.response?.status !== 429) {
        throw error
      }
      await new Promise((resolve) => setTimeout(resolve, delay))
      return retryRequest(fn, retries - 1, delay * 2)
    }
  }

  const getStatistics = async () => {
    setStatisticsData(defaultStatisticsData)

    const partnerUuid = partner?.uuid

    try {
      const [distance, Co2Data, tripDetail, speed] = await Promise.all([
        retryRequest(() => getDistance(partnerUuid, fleet?.uuid)),
        retryRequest(() => getCo2(partnerUuid, fleet?.uuid)),
        retryRequest(() => getTrips(partnerUuid, fleet?.uuid)),
        retryRequest(() => getSpeed(partnerUuid, fleet?.uuid)),
      ])

      const analysisData: STATISTICS_DATA = {
        devices_count: "",
        trip_count: "",
        movement_count: "",
        movement_lasthour_count: "",
        valid_position_count: "",
        valid_position_lastday_count: "",
        no_servicebooking_count: "",
        expiring_servicebooking_count: "",
        critical_battery_devices_count: "",
        locked_devices_count: "",
        unlocked_devices_count: "",
        theft_reported_devices_count: "",
        avg_distance: `${distanceConversion(distance?.data?.avg_distance || 0)}`,
        total_distance: `${distanceConversion(distance?.data?.total_distance || 0)}`,
        avg_co2: `${Math.round(Co2Data?.data?.avg_co2 || 0)} ${Co2Data?.data?.avg_co2_unit || ""}`,
        total_co2: `${Math.round(Co2Data?.data?.total_co2 || 0)} ${Co2Data?.data?.total_co2_unit || ""}`,
        avg_trips: `${tripDetail?.data?.avg_trips || 0}`,
        total_trips: `${tripDetail?.data?.total_trips || 0}`,
        avg_speed: `${speed?.data?.avg_speed || 0} ${speed?.data?.avg_speed_unit || ""}`,
        total_speed: `${speed?.data?.total_speed || 0} ${speed?.data?.total_speed_unit || ""}`,
      }
      setStatisticsData(analysisData)
    } catch (error) {
      console.error("Error fetching statistics:", error)
    }
  }

  const refreshTopRowCounts = async () => {
    const partnerUuid = partner?.uuid
    if (!partnerUuid) return
    const selectedFleetUuid = fleet?.uuid
    const toNumber = (n: any) => (typeof n === "number" ? n : parseInt(n || "0", 10) || 0)
    try {
      const date = new Date()
      date.setDate(date.getDate() + 90)
      const yyyy = date.getFullYear()
      const mm = String(date.getMonth() + 1).padStart(2, "0")
      const dd = String(date.getDate()).padStart(2, "0")
      const expFilter = `${yyyy}-${mm}-${dd}`

      const [
        totalRes,
        activeRes,
        noServiceRes,
        lockedRes,
        stolenRes,
        expiringRes,
        criticalBatteryActiveRes,
      ] = await Promise.all([
        getDevice(partnerUuid, selectedFleetUuid, { page: 0, per_page: 1 }),
        getDevice(partnerUuid, selectedFleetUuid, {
          page: 0,
          per_page: 1,
          servicebooking_status: "active",
        }),
        getDevice(partnerUuid, selectedFleetUuid, {
          page: 0,
          per_page: 1,
          servicebooking_status: "none",
        }),
        getDevice(partnerUuid, selectedFleetUuid, { page: 0, per_page: 1, lock_state: "locked" }),
        getDevice(partnerUuid, selectedFleetUuid, { page: 0, per_page: 1, reported_stolen: true }),
        getDevice(partnerUuid, selectedFleetUuid, {
          page: 0,
          per_page: 1,
          servicebooking_status: "active",
          servicebooking_end_before: expFilter,
        }),
        getDevice(partnerUuid, selectedFleetUuid, {
          page: 0,
          per_page: 1,
          battery_below: 21,
          servicebooking_status: "active",
        }),
      ])
      const total = toNumber(totalRes?.data?.meta?.total)
      const active = toNumber(activeRes?.data?.meta?.total)
      const noService = toNumber(noServiceRes?.data?.meta?.total)
      const locked = toNumber(lockedRes?.data?.meta?.total)
      const stolen = toNumber(stolenRes?.data?.meta?.total)
      const expiring = toNumber(expiringRes?.data?.meta?.total)
      const criticalBatteryActive = toNumber(criticalBatteryActiveRes?.data?.meta?.total)
      setTopRowCounts({ total, active, locked, stolen, expiring, noService, criticalBatteryActive })
    } catch (e) {
      // non-fatal; keep defaults
      console.error("Failed to refresh top row counts", e)
      setTopRowCounts({
        total: 0,
        active: 0,
        locked: 0,
        stolen: 0,
        expiring: 0,
        noService: 0,
        criticalBatteryActive: 0,
      })
    }
  }

  const renderLoaderSpinner = () => {
    return (
      <>
        <div className={centerAlign} style={styles.backgroundDrop}>
          <div className={centerAlign} style={styles.loaderView}>
            <Spinner className="align-self-center" animation="border" size="sm" />
            <b className="m-2">{t("dashboard_screen.loader")}</b>
          </div>
        </div>
      </>
    )
  }

  const renderTopRow = () => {
    const date = new Date()
    date.setDate(date.getDate() + 90)
    const yyyy = date.getFullYear()
    const mm = String(date.getMonth() + 1).padStart(2, "0")
    const dd = String(date.getDate()).padStart(2, "0")
    const expFilter = `${yyyy}-${mm}-${dd}`
    return (
      <Row>
        <Col>
          <StatisticCard
            show={true}
            title={t("dashboard_screen.total_trackers")}
            totalCount={String(topRowCounts.total)}
            loading={loading}
            isDeviceActive={true}
            tooltip={t("dashboard_screen.tooltip.total_trackers")}
            onButtonClick={() => navigate(`/${partner?.uuid}/devices`)}
          />
          <StatisticCard
            show={true}
            title={t("dashboard_screen.devices_withouth_servicebooking")}
            tooltip={t("dashboard_screen.tooltip.devices_withouth_servicebooking")}
            totalCount={String(topRowCounts.noService)}
            loading={loading}
            onButtonClick={() => navigate(`/${partner?.uuid}/devices?servicebooking_status=none`)}
          />
        </Col>
        <Col>
          <StatisticCard
            show={true}
            title={t("dashboard_screen.devices_activated")}
            totalCount={String(topRowCounts.active)}
            loading={loading}
            tooltip={t("dashboard_screen.tooltip.devices_activated")}
            onButtonClick={() => navigate(`/${partner?.uuid}/devices?servicebooking_status=active`)}
          />
          <StatisticCard
            show={true}
            title={t("dashboard_screen.devices_with_ending_servicebookings", { value: "90" })}
            tooltip={t("dashboard_screen.tooltip.devices_with_ending_service")}
            totalCount={String(topRowCounts.expiring)}
            loading={loading}
            onButtonClick={() =>
              navigate(
                `/${partner?.uuid}/devices?servicebooking_status=active&servicebooking_end_before=${expFilter}`,
              )
            }
          />
        </Col>
        <Col>
          <StatisticCard
            show={true}
            title={t("dashboard_screen.locked_devices")}
            tooltip={t("dashboard_screen.tooltip.devices_locked")}
            totalCount={String(topRowCounts.locked)}
            loading={loading}
            onButtonClick={() => navigate(`/${partner?.uuid}/devices?lock_state=locked`)}
          />
          <StatisticCard
            show={true}
            title={t("dashboard_screen.devices_with_critical_battery")}
            tooltip={t("dashboard_screen.tooltip.critical_battery")}
            totalCount={String(topRowCounts.criticalBatteryActive)}
            loading={loading}
            onButtonClick={() =>
              navigate(
                `/${partner?.uuid}/devices?critical_battery=true&servicebooking_status=active`,
              )
            }
          />
        </Col>
        <Col>
          <StatisticCard
            show={true}
            title={t("dashboard_screen.reported_thefts")}
            tooltip={t("dashboard_screen.tooltip.reported_thefts")}
            totalCount={String(topRowCounts.stolen)}
            loading={loading}
            onButtonClick={() => navigate(`/${partner?.uuid}/devices?reported_stolen=true`)}
          />
        </Col>
      </Row>
    )
  }

  const renderTripsStatistics = () => {
    return (
      <>
        <label style={styles.cardTitle}> {t("dashboard_screen.trip_statistics")}</label>
        <Row>
          <Col>
            <StatisticCard
              show={true}
              title={t("dashboard_screen.average_trips")}
              tooltip={t("dashboard_screen.tooltip.devices_trip_avg")}
              totalCount={statisticsData.avg_trips}
              loading={loading}
            />
            <StatisticCard
              show={true}
              title={t("dashboard_screen.average_distance")}
              tooltip={t("dashboard_screen.tooltip.devices_distance_avg")}
              totalCount={statisticsData.avg_distance}
              loading={loading}
            />
          </Col>
          <Col>
            <StatisticCard
              show={true}
              title={t("dashboard_screen.total_trips")}
              tooltip={t("dashboard_screen.tooltip.devices_trip_total")}
              totalCount={statisticsData.total_trips}
              loading={loading}
            />
            <StatisticCard
              show={true}
              title={t("dashboard_screen.total_distance")}
              tooltip={t("dashboard_screen.tooltip.devices_distance_total")}
              totalCount={statisticsData.total_distance}
              loading={loading}
            />
          </Col>
          <Col>
            <StatisticCard
              show={true}
              title={t("dashboard_screen.average_co2")}
              tooltip={t("dashboard_screen.tooltip.co2_savings_avg")}
              totalCount={statisticsData.avg_co2}
              loading={loading}
            />
            <StatisticCard
              show={true}
              title={t("dashboard_screen.average_speed")}
              tooltip={t("dashboard_screen.tooltip.devices_speed")}
              totalCount={statisticsData.avg_speed}
              loading={loading}
            />
          </Col>
          <Col>
            <StatisticCard
              show={true}
              title={t("dashboard_screen.total_co2")}
              tooltip={t("dashboard_screen.tooltip.co2_savings_total")}
              totalCount={statisticsData.total_co2}
              loading={loading}
            />
          </Col>
        </Row>
      </>
    )
  }

  return (
    <Page showFleet showHeader isRelative headerValue={t("sidebar.statistics")}>
      {loading && renderLoaderSpinner()}
      {renderTopRow()}
      {renderTripsStatistics()}
    </Page>
  )
}
